import { useEffect, useRef } from "react";

import { trapTabKey } from "../../tasksShared/lib/focusTrap";

/**
 * Confirmation dialog for calendar Task delete.
 * Blocks Escape / backdrop close while pending.
 * Tab/Shift+Tab stay inside the dialog.
 */
function CalendarTaskDeleteDialog({
  open,
  task,
  pending,
  error,
  onConfirm,
  onCancel,
}) {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const timer = window.setTimeout(() => {
      confirmRef.current?.focus();
    }, 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (pending) {
          event.preventDefault();
          return;
        }
        onCancel();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === "function"
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, pending, onCancel]);

  if (!open || !task) return null;

  const title = task.title || "Задача";

  return (
    <div
      className="feature-tasks-calendar__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendar-task-delete-title"
      data-pending={pending ? "true" : "false"}
    >
      <div
        className="feature-tasks-calendar__modal-backdrop"
        aria-hidden="true"
        onClick={() => {
          if (!pending) onCancel();
        }}
      />
      <div
        ref={dialogRef}
        className="feature-tasks-calendar__modal-dialog feature-tasks-calendar__modal-dialog--confirm"
      >
        <header className="feature-tasks-calendar__modal-head">
          <h2
            id="calendar-task-delete-title"
            className="feature-tasks-calendar__modal-title"
          >
            Видалити задачу?
          </h2>
          <button
            type="button"
            className="feature-tasks-calendar__modal-close"
            onClick={onCancel}
            disabled={pending}
            aria-label="Закрити підтвердження видалення"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-tasks-calendar__modal-body">
          <p className="feature-tasks-calendar__confirm-text">
            Видалити задачу «{title}»? Цю дію не можна скасувати.
          </p>
          {error ? (
            <p
              className="feature-tasks-calendar__mutation-error"
              role="alert"
              aria-live="assertive"
            >
              {error}
            </p>
          ) : null}
        </div>
        <footer className="feature-tasks-calendar__modal-actions">
          <button
            type="button"
            className="action-btn"
            onClick={onCancel}
            disabled={pending}
          >
            Скасувати
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="action-btn action-btn--danger"
            onClick={onConfirm}
            disabled={pending}
            aria-busy={pending}
          >
            {pending ? "Видалення…" : "Видалити"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CalendarTaskDeleteDialog;
